/**
 * Button.tsx
 * 
 * This is a reusable button component that provides consistent styling
 * and behavior across the application. It supports:
 * 
 * - Multiple variants: primary, secondary, success, warning, error, info, ghost
 * - Different sizes: sm, md, lg
 * - Loading states with spinner
 * - Disabled states
 * - Custom className support
 * - All standard button props (onClick, type, etc.)
 * 
 * The component uses Tailwind CSS for styling and provides a clean,
 * accessible interface for all button interactions in the application.
 */

import React from 'react'
import { Loader2 } from 'lucide-react'
import { clsx } from 'clsx'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children: React.ReactNode
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

  const variantClasses = {
    primary: 'bg-primary-500 hover:bg-primary-600 text-white focus:ring-primary-500',
    secondary: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 focus:ring-primary-500',
    success: 'bg-success-500 hover:bg-success-600 text-white focus:ring-success-500',
    warning: 'bg-warning-500 hover:bg-warning-600 text-white focus:ring-warning-500',
    error: 'bg-error-500 hover:bg-error-600 text-white focus:ring-error-500',
    info: 'bg-info-500 hover:bg-info-600 text-white focus:ring-info-500',
    ghost: 'bg-transparent hover:bg-primary-50 text-primary-500 focus:ring-primary-500',
  }

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  }

  return (
    <button
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      )}
      {children}
    </button>
  )
}

export default Button 